import { EXPIRATION_BUFFER, getPinUnlockLocation, isAuthBootstrapSafeRoute, useAuth } from '~/entities/auth'
import { permissions } from '~/shared/lib'

let accessTokenTimer: ReturnType<typeof setTimeout> | null = null
let handleTokenExpiration: (() => Promise<void>) | null = null

function stopAccessTokenTimer() {
  if (accessTokenTimer) {
    clearTimeout(accessTokenTimer)
  }

  accessTokenTimer = null
}

export default defineNuxtPlugin(async () => {
  stopAccessTokenTimer()

  const route = useRoute()
  const router = useRouter()
  const {
    isTokenExpired,
    isAuthenticated,
    expiresIn,
    logout,
    ensureValidAccessToken,
    shouldRequirePinUnlock,
    syncLoginCodeFlagIfNeeded,
  } = useAuth()

  function stopTimer() {
    stopAccessTokenTimer()
  }

  async function redirectToPinUnlockIfNeeded() {
    await syncLoginCodeFlagIfNeeded()

    if (!shouldRequirePinUnlock()) {
      return false
    }

    stopTimer()

    if (route.path !== permissions.AUTH.PIN.ROUTE) {
      await router.replace(getPinUnlockLocation({
        fullPath: route.fullPath,
        queryRedirect: route.query.redirect,
      }))
    }

    return true
  }

  function setTokenExpirationTimer() {
    const accessExpiration = Number(expiresIn.value)

    if (
      !expiresIn.value
      || expiresIn.value === '0'
      || Number.isNaN(accessExpiration)
      || accessExpiration <= 0
    ) {
      return
    }

    const currentTime = Date.now()
    const timeToAccessTokenExpiration = accessExpiration - currentTime

    stopTimer()

    const delay = Math.max(timeToAccessTokenExpiration - EXPIRATION_BUFFER, 0)

    accessTokenTimer = setTimeout(() => {
      void handleTokenExpiration?.()
    }, delay)
  }

  async function checkTokenExpiration() {
    if (!isAuthenticated.value) {
      return
    }

    if (await redirectToPinUnlockIfNeeded()) {
      return
    }

    try {
      if (isTokenExpired()) {
        const isValid = await ensureValidAccessToken()

        if (!isValid) {
          if (await redirectToPinUnlockIfNeeded()) {
            return
          }

          throw new Error('Failed to refresh token')
        }
      }
    }
    catch {
      stopTimer()

      if (!isAuthBootstrapSafeRoute(route.path)) {
        await logout({ redirect: route.fullPath as string })
      }

      return
    }

    setTokenExpirationTimer()
  }

  handleTokenExpiration = checkTokenExpiration

  async function handleVisibilityChange() {
    if (document.hidden || !isAuthenticated.value) {
      return
    }

    if (await redirectToPinUnlockIfNeeded()) {
      return
    }

    const isValid = await ensureValidAccessToken()

    if (!isValid) {
      if (await redirectToPinUnlockIfNeeded()) {
        return
      }

      if (!isAuthBootstrapSafeRoute(route.path)) {
        await logout({ redirect: route.fullPath as string })
      }

      return
    }

    setTokenExpirationTimer()
  }

  async function bootstrapAuthSession() {
    if (!isAuthenticated.value) {
      stopTimer()
      return
    }

    if (await redirectToPinUnlockIfNeeded()) {
      return
    }

    const isValid = await ensureValidAccessToken()

    if (!isValid) {
      if (await redirectToPinUnlockIfNeeded()) {
        return
      }

      if (!isAuthBootstrapSafeRoute(route.path)) {
        await logout({ redirect: route.fullPath as string })
      }

      return
    }

    setTokenExpirationTimer()
  }

  if (import.meta.client) {
    document.addEventListener('visibilitychange', () => {
      void handleVisibilityChange()
    })

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        void handleVisibilityChange()
      }
    })
  }

  watch(isAuthenticated, () => {
    void bootstrapAuthSession()
  })

  watch(expiresIn, () => {
    if (isAuthenticated.value && !shouldRequirePinUnlock()) {
      setTokenExpirationTimer()
    }
  })

  await bootstrapAuthSession()
})
